import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { INTERVIEW_STEPS } from 'ducks/router'
import { STATIC_PAGES } from '../model'

// NOTE: hook that handles state and logic to maintain
// steps transition, switching mode from read only to write,
// etc. and exposes state and handlers for other local hooks
// and the interview components
export const useStepsState = state => {
  const { current_status, evaluation, sections } = state

  const [activeStep, setActiveStep] = React.useState(null)

  const { status } = evaluation ?? {}

  const isReadOnly = R.or(R.isEmpty(evaluation), R.isNil(evaluation))

  const activeSection = React.useMemo(
    () =>
      R.find(
        section => R.equals(section?.key, activeStep) && section,
        sections,
      ) ?? {},
    [activeStep, sections],
  )

  const steps = React.useMemo(() => {
    const summary = [{ key: INTERVIEW_STEPS.SUMMARY, title: t`Summary` }]

    return R.map(
      ({ key, scoreable, title }) => {
        const isActive = R.equals(activeStep, key)

        return {
          key,
          label: title,
          scoreable,
          isActive,
        }
      },
      R.concat(sections, !isReadOnly ? summary : []),
    )
  }, [activeStep, sections, isReadOnly])

  const currentStepIndex = React.useMemo(
    () => R.findIndex(R.propEq('key', activeStep))(steps),
    [activeStep, steps],
  )

  const isNextStepDisabled = R.equals(activeStep, R.last(steps)?.key)
  const isPreviousStepDisabled = R.equals(activeStep, steps[0]?.key)

  const isCategoryStep = R.includes(
    activeStep,
    R.map(({ key }) => key, steps),
  )
  const isIntroductionStep = R.equals(activeStep, INTERVIEW_STEPS.INTRODUCTION)
  const isReportStep = R.equals(activeStep, INTERVIEW_STEPS.REPORT)
  const isSummaryStep = R.equals(activeStep, INTERVIEW_STEPS.SUMMARY)
  const isStaticStep = R.includes(activeStep, STATIC_PAGES)

  const handleChangeStep = React.useCallback(
    step => {
      setActiveStep(step)
    },
    [setActiveStep],
  )

  const handleChangeNextStep = React.useCallback(() => {
    if (R.gte(currentStepIndex, R.length(steps) - 1)) return

    const nextStep = steps[currentStepIndex + 1]?.key

    handleChangeStep(nextStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangePreviousStep = React.useCallback(() => {
    if (R.lte(currentStepIndex, 0)) return

    const previousStep = steps[currentStepIndex - 1]?.key

    handleChangeStep(previousStep)
  }, [currentStepIndex, steps, handleChangeStep])

  React.useEffect(() => {
    if (R.isNil(steps) || R.isEmpty(steps) || !R.isNil(activeStep)) return

    R.or(R.equals(status, 4), R.equals(current_status, 5))
      ? handleChangeStep(INTERVIEW_STEPS.REPORT)
      : handleChangeStep(INTERVIEW_STEPS.INTRODUCTION)
  }, [activeStep, current_status, status, steps, handleChangeStep])

  return {
    state: {
      activeSection,
      activeStep,
      steps,
      isCategoryStep,
      isIntroductionStep,
      isNextStepDisabled,
      isPreviousStepDisabled,
      isReadOnly,
      isReportStep,
      isSummaryStep,
      isStaticStep,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
    },
  }
}
